import _ from 'lodash';
import { currencyFormattedAmount } from 'helpers/currency';

// Line item maths for the work order, estimate and invoice editors (one place
// instead of a copy in every row component). Same rules, value types and
// display fields as the old row components, which recalculated on mount and on
// every change, and whose results get saved with the record:
//
// - a row's total is qty × rate × markup, or qty × rate when there's no markup;
// - an "Other" line tied to a default line item (percentage of labour,
//   materials, or both) takes its rate from those totals unless overridden,
//   recalculated only when the rows it depends on exist (the old rows did it
//   from their own lifecycle, so with no labour rows a "% of labour" line kept
//   its stored rate);
// - a "Truck Charge" line with the tenant's truck charge on is (qty / 4) × rate;
// - quantity and rate keep the text the user typed (without "$" or thousands
//   separators), with numbers and display values alongside, as before.

const pf = (value) => parseFloat(value);

export const rowTotal = (item) => (item.markup
  ? pf(item.quantity) * pf(item.rate_per_unit) * pf(item.markup)
  : pf(item.quantity) * pf(item.rate_per_unit));

// A total as a number for display and sums (blank rows give NaN, shown as 0).
export const amount = (value) => {
  const number = pf(value);
  return Number.isFinite(number) ? number : 0;
};

const setTotal = (item, total) => {
  item.total = total;
  item.total_display_value = currencyFormattedAmount(total);
};

const truckChargeTotal = (item, truckCharge) => (pf(item.quantity) / 4) * pf(truckCharge.rate);

export const isTruckCharge = (item, truckCharge) => item.description === 'Truck Charge' && Boolean(truckCharge && truckCharge.enabled);

const customTotal = (item, truckCharge) => (isTruckCharge(item, truckCharge) ? truckChargeTotal(item, truckCharge) : rowTotal(item));

const PERCENTAGE_SOURCES = {
  percentage_of_labour: ['labour'],
  percentage_of_material: ['materials'],
  percentage_of_materials_labour: ['labour', 'materials'],
};

// Recalculate every row (mutates `attributes`). `truckCharge` is the current
// user's tenant setting ({ enabled, rate }).
export const recalculate = (attributes, { truckCharge } = {}) => {
  const labour = attributes.labour || [];
  const materials = attributes.materials || [];
  const custom = attributes.custom_line_items || [];
  labour.forEach((item) => setTotal(item, rowTotal(item)));
  materials.forEach((item) => setTotal(item, rowTotal(item)));
  const sums = { labour: _.sumBy(labour, 'total'), materials: _.sumBy(materials, 'total') };
  const present = { labour: labour.length > 0, materials: materials.length > 0 };
  custom.forEach((item) => {
    const sources = PERCENTAGE_SOURCES[item.default_line_item_type];
    if (sources && !item.override && sources.some((source) => present[source])) {
      const base = sources.reduce((sum, source) => sum + sums[source], 0);
      item.rate_per_unit = (base * item.default_line_item_value) / 100;
      item.rate_per_unit_display_value = currencyFormattedAmount(item.rate_per_unit);
    }
    setTotal(item, customTotal(item, truckCharge));
  });
  return attributes;
};

// Turning Override off on a work order's default line puts every default
// line not overridden back to its default rate: the percentage, or the fixed
// dollar value (as the old Override checkbox did).
export const restoreDefaultRates = (attributes, { truckCharge } = {}) => {
  (attributes.custom_line_items || []).forEach((item) => {
    if (!item.default_line_item_id || item.override) return;
    const sources = PERCENTAGE_SOURCES[item.default_line_item_type];
    if (sources) {
      const base = sources.reduce((sum, source) => sum + _.sumBy(attributes[source] || [], 'total'), 0);
      item.rate_per_unit = (base * item.default_line_item_value) / 100;
    } else if (item.default_line_item_type === 'dollar_value') {
      item.rate_per_unit = item.default_line_item_value;
    }
    item.rate_per_unit_display_value = currencyFormattedAmount(item.rate_per_unit);
    setTotal(item, customTotal(item, truckCharge));
  });
};

// Edits, writing the same fields (and types) the old inputs wrote.
// Thousands separators are ignored ("3,500" is 3500, not 3).
const plain = (raw) => String(raw).replace(/\$/g, '').replace(/,/g, '');

export const setQuantity = (item, raw) => {
  item.quantity_display_value = raw === '' ? 0 : plain(raw);
  item.quantity = pf(item.quantity_display_value);
};

export const setRate = (item, raw) => {
  const value = raw === '' ? 0 : plain(raw);
  item.rate_per_unit = value;
  item.rate_per_unit_display_value = currencyFormattedAmount(item.rate_per_unit);
};

export const setMarkup = (item, raw, { flagOverride = false } = {}) => {
  if (flagOverride) item.markup_override = true;
  item.markup = raw === '' ? 1 : pf(raw);
};

// Tax on a line: each of its tax lines' percentage of its total.
export const lineTax = (item) => _.sumBy(item.tax_line_items || [], (tax) => (pf(tax.percentage_value) / 100) * amount(item.total));

export const GROUPS = ['labour', 'materials', 'custom_line_items'];

// A set of lines' figures before and after markup (the old section footers:
// sub total before markup, sub total, gross markup and gross margin).
export const figures = (items = []) => {
  const subtotal = _.sumBy(items, (item) => amount(item.total));
  const cost = _.sumBy(items, (item) => amount(pf(item.quantity) * pf(item.rate_per_unit)));
  const margin = subtotal - cost;
  return { subtotal, cost, margin, markupPercent: cost > 0 ? Math.round((margin / cost) * 100) : null };
};

// The document's figures, from the rows as they are now.
export const documentTotals = (attributes) => {
  const groups = {};
  let subtotal = 0;
  let tax = 0;
  let cost = 0;
  GROUPS.forEach((group) => {
    const items = attributes[group] || [];
    const total = _.sumBy(items, (item) => amount(item.total));
    groups[group] = total;
    subtotal += total;
    tax += _.sumBy(items, lineTax);
    cost += _.sumBy(items, (item) => amount(pf(item.quantity) * pf(item.rate_per_unit)));
  });
  const grand = subtotal + tax;
  const paid = _.sumBy((attributes.transactions || []).filter((transaction) => transaction.status === 'paid'), (transaction) => amount(transaction.amount_paid));
  return {
    groups,
    subtotal,
    tax,
    grand,
    cost,
    margin: subtotal - cost,
    markupPercent: cost > 0 ? Math.round(((subtotal - cost) / cost) * 100) : null,
    paid,
    due: grand - paid,
  };
};
